import React, { useCallback, useEffect, useRef, useState } from 'react'

import type { RubroRow } from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'

import type { DocumentationSlotKey } from '../../../../../utils/conversationalMode/documentationSlotSummaryUtils.ts'

import {

  isMetricOnlyReviewScreen,

  isQuotationTabEnabled,

  isTechnicalSpecTabEnabled,

  type DocumentationReviewContext

} from '../../../../../utils/conversationalMode/projectAutofillWorkflow.ts'

import MetricSheetCompactEntry from './MetricSheetCompactEntry.tsx'

import BudgetSheetCompactEntry from './BudgetSheetCompactEntry.tsx'

import TechnicalSpecCompactEntry from './TechnicalSpecCompactEntry.tsx'

import { restoreMetricRemovalPackage } from '../../../../../utils/conversationalMode/rubroMetricBudgetCoherenceUtils.ts'

import type { RubroSlotOriginHandlers } from './RubroSlotOriginHandlers.ts'

import type { RubroDeliverableSourceLineProps } from './rubroDeliverableSourceLineProps.ts'

import styles from './RubroSheetTabs.module.scss'

import ProjectAutofillInfoNote from './ProjectAutofillInfoNote.tsx'

import type { MetricBudgetSyncPhase } from './projectAutofillCard/hooks/useRubroMetricBudgetSilentSync.ts'



type TabId = 'technical' | 'metric' | 'quotation'



type SourceLineProps = RubroDeliverableSourceLineProps



type Props = {

  tempId: string

  rubro: RubroRow

  draftId: string | undefined

  draftLocked: boolean

  isSaving: boolean

  documentationReview: DocumentationReviewContext

  updateRubro: (tempId: string, updates: Partial<Omit<RubroRow, 'tempId'>>) => void

  onOpenMetricSheetFullscreen: (tempId: string) => void

  onOpenBudgetSheetFullscreen: (tempId: string) => void

  onOpenTechnicalSpecFullscreen: (tempId: string) => void

  sourceLineProps: SourceLineProps

  originHandlers: RubroSlotOriginHandlers

  getMetricBudgetSyncPhase?: (tempId: string) => MetricBudgetSyncPhase

  draftAnalyzed?: boolean

  onImportMetricEdify?: (tempId: string) => void

  isMetricImporting?: (tempId: string) => boolean

  metricImportError?: (tempId: string) => string | null | undefined

}



const RubroSheetTabs: React.FC<Props> = ({

  tempId,

  rubro,

  draftId,

  draftLocked,

  isSaving,

  documentationReview,

  updateRubro,

  onOpenMetricSheetFullscreen,

  onOpenBudgetSheetFullscreen,

  onOpenTechnicalSpecFullscreen,

  sourceLineProps,

  originHandlers,

  getMetricBudgetSyncPhase,

  draftAnalyzed = false,

  onImportMetricEdify,

  isMetricImporting,

  metricImportError

}) => {

  const budgetSyncPhase = getMetricBudgetSyncPhase?.(tempId) ?? 'idle'

  const technicalEnabled = isTechnicalSpecTabEnabled(documentationReview, rubro.technicalSpecEligibility)

  const quotationEnabled = isQuotationTabEnabled(documentationReview.reviewStage)

  const metricOnlyReview = isMetricOnlyReviewScreen(documentationReview.reviewStage)

  const [activeTab, setActiveTab] = useState<TabId>('technical')

  const [technicalOriginPickerOpen, setTechnicalOriginPickerOpen] = useState(false)

  const [metricOriginPickerOpen, setMetricOriginPickerOpen] = useState(false)

  const [quotationOriginPickerOpen, setQuotationOriginPickerOpen] = useState(false)

  type RemovedSnapshotsMap = Partial<Record<DocumentationSlotKey, Partial<Omit<RubroRow, 'tempId'>>>>

  const removedSnapshotsByRubroRef = useRef<Record<string, RemovedSnapshotsMap>>({})

  const [removedSnapshots, setRemovedSnapshots] = useState<RemovedSnapshotsMap>({})

  useEffect(() => {
    setTechnicalOriginPickerOpen(false)
    setMetricOriginPickerOpen(false)
    setQuotationOriginPickerOpen(false)
    setRemovedSnapshots(removedSnapshotsByRubroRef.current[tempId] ?? {})
  }, [tempId])

  const persistRemovedSnapshots = useCallback((tempIdKey: string, next: RemovedSnapshotsMap) => {
    if (Object.keys(next).length === 0) {
      delete removedSnapshotsByRubroRef.current[tempIdKey]
    } else {
      removedSnapshotsByRubroRef.current[tempIdKey] = next
    }
  }, [])

  const setRemovedSnapshot = (slot: DocumentationSlotKey, snapshot: Partial<Omit<RubroRow, 'tempId'>> | null) => {
    setRemovedSnapshots((prev) => {
      const next = { ...prev }
      if (snapshot) next[slot] = snapshot
      else delete next[slot]
      persistRemovedSnapshots(tempId, next)
      return next
    })
  }



  const handleRestoreMetricPackage = () => {

    const snap = removedSnapshots.metricComputation

    if (!snap) return

    updateRubro(
      tempId,
      restoreMetricRemovalPackage(rubro, snap)
    )

    setRemovedSnapshot('metricComputation', null)

    setMetricOriginPickerOpen(false)

  }



  const effectiveTab: TabId = (() => {

    if (activeTab === 'quotation' && !quotationEnabled) {

      return technicalEnabled ? 'technical' : 'metric'

    }

    if (activeTab === 'technical' && !technicalEnabled) return 'metric'

    return activeTab

  })()



  return (

    <div className={styles.tabs}>

      <div className={styles.tabList} role="tablist" aria-label="Documentación del rubro">

        <button

          type="button"

          role="tab"

          aria-selected={effectiveTab === 'technical'}

          aria-disabled={!technicalEnabled}

          className={[styles.tabBtn, effectiveTab === 'technical' ? styles.tabBtnActive : '']

            .filter(Boolean)

            .join(' ')}

          disabled={!technicalEnabled}

          onClick={() => technicalEnabled && setActiveTab('technical')}

        >

          Especificaciones técnicas

        </button>

        <button

          type="button"

          role="tab"

          aria-selected={effectiveTab === 'metric'}

          className={[styles.tabBtn, effectiveTab === 'metric' ? styles.tabBtnActive : ''].filter(Boolean).join(' ')}

          onClick={() => setActiveTab('metric')}

        >

          Cómputo métrico

        </button>

        <button

          type="button"

          role="tab"

          aria-selected={effectiveTab === 'quotation'}

          aria-disabled={!quotationEnabled}

          className={[styles.tabBtn, effectiveTab === 'quotation' ? styles.tabBtnActive : ''].filter(Boolean).join(' ')}

          disabled={!quotationEnabled}

          onClick={() => quotationEnabled && setActiveTab('quotation')}

        >

          Planilla de cotización

        </button>

      </div>

      {effectiveTab === 'technical' ? (

        <div className={styles.tabPanel} role="tabpanel">

          <TechnicalSpecCompactEntry

            tempId={tempId}

            rubro={rubro}

            draftLocked={draftLocked}

            isSaving={isSaving}

            documentationReview={documentationReview}

            updateRubro={updateRubro}

            onOpenFullscreen={onOpenTechnicalSpecFullscreen}

            sourceLineProps={sourceLineProps}

            originHandlers={originHandlers}

            originPickerOpen={technicalOriginPickerOpen}

            onOriginPickerOpenChange={setTechnicalOriginPickerOpen}

            removedSnapshot={removedSnapshots.technicalSpecifications ?? null}

            onRemovedSnapshotChange={(snap) => setRemovedSnapshot('technicalSpecifications', snap)}

          />

        </div>

      ) : effectiveTab === 'metric' ? (

        <div className={styles.tabPanel} role="tabpanel">

          <MetricSheetCompactEntry

            tempId={tempId}

            rubro={rubro}

            draftLocked={draftLocked}

            isSaving={isSaving}

            draftAnalyzed={draftAnalyzed}

            updateRubro={updateRubro}

            onOpenFullscreen={onOpenMetricSheetFullscreen}

            sourceLineProps={sourceLineProps}

            originHandlers={originHandlers}

            originPickerOpen={metricOriginPickerOpen}

            onOriginPickerOpenChange={setMetricOriginPickerOpen}

            removedSnapshot={removedSnapshots.metricComputation ?? null}

            onRemovedSnapshotChange={(snap) => setRemovedSnapshot('metricComputation', snap)}

            onImportMetricEdify={onImportMetricEdify}

            importLoading={isMetricImporting?.(tempId)}

            importError={metricImportError?.(tempId)}

          />

        </div>

      ) : (

        <div className={styles.tabPanel} role="tabpanel">

          {metricOnlyReview ? (

            <ProjectAutofillInfoNote className={styles.tabLockedHint}>

              {documentationReview.sheetGenerationOutcome === 'failed'

                ? 'No generamos la planilla de cotización automáticamente. Revisá el cómputo o re-analizá el documento.'

                : 'Re-analizá el documento para generar las planillas de cotización automáticamente.'}

            </ProjectAutofillInfoNote>

          ) : null}

          <BudgetSheetCompactEntry

            embedded

            tempId={tempId}

            rubro={rubro}

            draftId={draftId}

            draftLocked={draftLocked}

            isSaving={isSaving}

            documentationReview={documentationReview}

            updateRubro={updateRubro}

            onOpenFullscreen={onOpenBudgetSheetFullscreen}

            sourceLineProps={sourceLineProps}

            originHandlers={originHandlers}

            originPickerOpen={quotationOriginPickerOpen}

            onOriginPickerOpenChange={setQuotationOriginPickerOpen}

            removedSnapshot={removedSnapshots.budget ?? null}

            onRemovedSnapshotChange={(snap) => setRemovedSnapshot('budget', snap)}

            metricRestoreSnapshot={removedSnapshots.metricComputation ?? null}

            onRestoreMetricPackage={handleRestoreMetricPackage}

            budgetSyncPhase={budgetSyncPhase}

          />

        </div>

      )}

    </div>

  )

}



export default RubroSheetTabs


